import { useEffect, useRef, useState } from 'react'

type ComplianceBadgeProps = {
  framework: string
  formCode: string
  formName?: string
  section?: string
  entryCount?: number
  incompleteCount?: number
  violationCount?: number
  /** Human-readable gaps and violations, shown in the popover. */
  issues?: string[]
  /** Where the popover opens: below the pill (default), or above it, e.g. at the bottom of a dialog. */
  placement?: 'below' | 'above'
  /** Leave out the entry-count line, for a badge about a single entry (an Add/Edit dialog). */
  hideCounts?: boolean
}

const MAX_ISSUES = 5

const FRAMEWORK_STYLES: Record<string, string> = {
  CanadaGAP:
    'bg-emerald-50 text-emerald-700 ring-emerald-600/20 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:ring-emerald-500/20 dark:hover:bg-emerald-900/30',
  FSMA:
    'bg-blue-50 text-blue-700 ring-blue-600/20 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:ring-blue-500/20 dark:hover:bg-blue-900/30',
  USDA:
    'bg-amber-50 text-amber-700 ring-amber-600/20 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-400 dark:ring-amber-500/20 dark:hover:bg-amber-900/30',
}

const DEFAULT_STYLE =
  'bg-zinc-100 text-zinc-700 ring-zinc-400/30 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:ring-zinc-700/30 dark:hover:bg-zinc-700/50'

export function ComplianceBadge({
  framework,
  formCode,
  formName,
  section,
  entryCount,
  incompleteCount,
  violationCount,
  issues = [],
  placement = 'below',
  hideCounts = false,
}: ComplianceBadgeProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const hasViolation = violationCount !== undefined && violationCount > 0
  const hasWarning = (incompleteCount !== undefined && incompleteCount > 0) || issues.length > 0
  const isComplete = entryCount !== undefined && entryCount > 0 && !hasWarning && !hasViolation
  const pillStyle = FRAMEWORK_STYLES[framework] ?? DEFAULT_STYLE

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div ref={ref} className="relative mt-1 inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${pillStyle}`}
      >
        {framework} {formCode}
        {hasViolation ? (
          <span className="text-red-500 dark:text-red-400">⚠</span>
        ) : hasWarning ? (
          <span className="text-amber-500 dark:text-amber-400">⚠</span>
        ) : (
          isComplete && <span aria-label="complete">✓</span>
        )}
      </button>
      {open && (
        <div className={`absolute left-0 z-10 w-max ${placement === 'above' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'} min-w-48 max-w-80 rounded-lg bg-white p-3 shadow-lg ring-1 ring-zinc-950/10 dark:bg-zinc-800 dark:ring-white/10`}>
          <p className="text-xs font-semibold text-zinc-950 dark:text-white">
            {framework} {formCode}
          </p>
          {formName && <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">{formName}</p>}
          {section && <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">{section}</p>}
          {entryCount !== undefined && !hideCounts && (
            <p className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-400">
              {entryCount} {entryCount === 1 ? 'entry' : 'entries'}
              {incompleteCount !== undefined && incompleteCount > 0 && (
                <span className="ml-1 text-amber-600 dark:text-amber-400">· {incompleteCount} incomplete</span>
              )}
              {hasViolation && <span className="ml-1 text-red-600 dark:text-red-400">· {violationCount} flagged</span>}
            </p>
          )}
          {issues.length > 0 && (
            <ul className="mt-1.5 space-y-1 border-t border-zinc-950/5 pt-1.5 dark:border-white/10">
              {issues.slice(0, MAX_ISSUES).map((issue, i) => (
                <li key={i} className="text-xs text-zinc-600 dark:text-zinc-300">
                  {issue}
                </li>
              ))}
              {issues.length > MAX_ISSUES && (
                <li className="text-xs text-zinc-400 dark:text-zinc-500">+{issues.length - MAX_ISSUES} more</li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
